// src/indicator.schema.ts
import { z } from "zod";
var indicatorPeriod = z.number().int("Period must be a whole number.").min(1, "Period must be at least 1.").max(500, "Period cannot exceed 500.");
var priceSourceSchema = z.enum(["open", "high", "low", "close", "median", "typical", "weighted"]);
var shiftSchema = z.number().int().min(0).max(100).optional().default(0);
var smaParamsSchema = z.object({
  type: z.literal("SMA"),
  period: indicatorPeriod,
  priceSource: priceSourceSchema.optional().default("close"),
  shift: shiftSchema
});
var emaParamsSchema = z.object({
  type: z.literal("EMA"),
  period: indicatorPeriod,
  priceSource: priceSourceSchema.optional().default("close"),
  shift: shiftSchema
});
var wmaParamsSchema = z.object({
  type: z.literal("WMA"),
  period: indicatorPeriod,
  priceSource: priceSourceSchema.optional().default("close"),
  shift: shiftSchema
});
var smmaParamsSchema = z.object({
  type: z.literal("SMMA"),
  period: indicatorPeriod,
  priceSource: priceSourceSchema.optional().default("close"),
  shift: shiftSchema
});
var rsiParamsSchema = z.object({
  type: z.literal("RSI"),
  period: indicatorPeriod,
  priceSource: priceSourceSchema.optional().default("close"),
  shift: shiftSchema
});
var macdParamsSchema = z.object({
  type: z.literal("MACD"),
  fastPeriod: z.number().int().min(1).max(500),
  slowPeriod: z.number().int().min(1).max(500),
  signalPeriod: z.number().int().min(1).max(500),
  priceSource: priceSourceSchema.optional().default("close"),
  outputLine: z.enum(["main", "signal", "histogram"]).optional().default("main")
});
var bbParamsSchema = z.object({
  type: z.literal("BB"),
  period: indicatorPeriod,
  deviations: z.number().min(0.1).max(10).optional().default(2),
  priceSource: priceSourceSchema.optional().default("close"),
  shift: shiftSchema,
  outputBand: z.enum(["upper", "middle", "lower"]).optional().default("upper")
});
var stochParamsSchema = z.object({
  type: z.literal("STOCH"),
  kPeriod: z.number().int().min(1).max(500),
  dPeriod: z.number().int().min(1).max(500),
  slowing: z.number().int().min(1).max(500),
  priceField: z.enum(["low_high", "close_close"]).optional().default("low_high"),
  maType: z.enum(["SMA", "EMA"]).optional().default("SMA"),
  outputLine: z.enum(["k", "d"]).optional().default("k")
});
var atrParamsSchema = z.object({
  type: z.literal("ATR"),
  period: indicatorPeriod,
  shift: shiftSchema
});
var adxParamsSchema = z.object({
  type: z.literal("ADX"),
  period: indicatorPeriod,
  outputLine: z.enum(["adx", "plusDI", "minusDI"]).optional().default("adx")
});
var cciParamsSchema = z.object({
  type: z.literal("CCI"),
  period: indicatorPeriod,
  priceSource: priceSourceSchema.optional().default("typical"),
  shift: shiftSchema
});
var ichimokuParamsSchema = z.object({
  type: z.literal("ICHIMOKU"),
  tenkanPeriod: z.number().int().min(1).max(500).optional().default(9),
  kijunPeriod: z.number().int().min(1).max(500).optional().default(26),
  senkouBPeriod: z.number().int().min(1).max(500).optional().default(52),
  outputLine: z.enum(["tenkan", "kijun", "senkou_a", "senkou_b", "chikou"]).optional().default("tenkan")
});
var psarParamsSchema = z.object({
  type: z.literal("PSAR"),
  step: z.number().min(1e-3).max(1).optional().default(0.02),
  maximum: z.number().min(1e-3).max(1).optional().default(0.2)
});
var momentumParamsSchema = z.object({
  type: z.literal("MOMENTUM"),
  period: indicatorPeriod,
  priceSource: priceSourceSchema.optional().default("close"),
  shift: shiftSchema
});
var customIndicatorParamSchema = z.object({
  name: z.string().min(1).max(50),
  type: z.enum(["int", "double", "string", "bool"]),
  value: z.union([z.number(), z.string(), z.boolean()])
});
var customParamsSchema = z.object({
  type: z.literal("CUSTOM"),
  indicatorName: z.string().min(1, "Indicator name cannot be empty.").max(100),
  bufferIndex: z.number().int().min(0, "Buffer index must be 0 or higher.").max(64),
  bufferLabel: z.string().max(100).optional(),
  customParams: z.array(customIndicatorParamSchema).optional().default([]),
  shift: shiftSchema
});
var indicatorParamsSchema = z.discriminatedUnion("type", [
  smaParamsSchema,
  emaParamsSchema,
  wmaParamsSchema,
  smmaParamsSchema,
  rsiParamsSchema,
  macdParamsSchema,
  bbParamsSchema,
  stochParamsSchema,
  atrParamsSchema,
  adxParamsSchema,
  cciParamsSchema,
  ichimokuParamsSchema,
  psarParamsSchema,
  momentumParamsSchema,
  customParamsSchema
]);
var indicatorInstanceSchema = z.object({
  id: z.string().min(1).regex(/^[a-zA-Z0-9_-]+$/, "ID must be alphanumeric with underscores or hyphens."),
  label: z.string().min(1).max(100).optional().default("Indicator"),
  params: indicatorParamsSchema
});

// src/rule.schema.ts
import { z as z2 } from "zod";
var indicatorOperandSchema = z2.object({
  kind: z2.literal("indicator"),
  indicatorId: z2.string().min(1)
});
var priceOperandSchema = z2.object({
  kind: z2.literal("price"),
  field: z2.enum(["open", "high", "low", "close"])
});
var constantOperandSchema = z2.object({
  kind: z2.literal("constant"),
  value: z2.number().finite("Constant value must be a finite number.")
});
var conditionOperandSchema = z2.discriminatedUnion("kind", [
  indicatorOperandSchema,
  priceOperandSchema,
  constantOperandSchema
]);
var conditionOperatorSchema = z2.enum([
  "crosses_above",
  "crosses_below",
  "greater_than",
  "less_than",
  "equal_within",
  "rising",
  "falling",
  "inside_bands",
  "outside_bands"
]);
var strategyConditionSchema = z2.object({
  id: z2.string().min(1, "Condition ID must not be empty."),
  left: conditionOperandSchema,
  operator: conditionOperatorSchema,
  right: conditionOperandSchema,
  nBars: z2.number().int().min(1).max(500).optional()
}).superRefine((condition, ctx) => {
  if ((condition.operator === "rising" || condition.operator === "falling") && condition.nBars === void 0) {
    ctx.addIssue({
      code: z2.ZodIssueCode.custom,
      message: `The "${condition.operator}" operator requires the "nBars" parameter.`,
      path: ["nBars"]
    });
  }
  if ((condition.operator === "inside_bands" || condition.operator === "outside_bands") && condition.right.kind !== "indicator") {
    ctx.addIssue({
      code: z2.ZodIssueCode.custom,
      message: `The "${condition.operator}" operator requires a Bollinger Bands indicator as the right operand.`,
      path: ["right"]
    });
  }
});
var candlePatternSchema = z2.enum([
  "bullish_engulfing",
  "bearish_engulfing",
  "bullish_pin_bar",
  "bearish_pin_bar",
  "doji",
  "inside_bar"
]);
var directionalRulesSchema = z2.object({
  logic: z2.enum(["AND", "OR"]),
  conditions: z2.array(strategyConditionSchema).max(20, "A maximum of 20 conditions is allowed per rule set."),
  patterns: z2.array(candlePatternSchema).optional()
});
function validateIndicatorReferences(rules, knownIndicatorIds) {
  const errors = [];
  rules.conditions.forEach((condition, index) => {
    if (condition.left.kind === "indicator" && !knownIndicatorIds.has(condition.left.indicatorId)) {
      errors.push(
        `Condition ${index + 1}: Left operand references unknown indicator "${condition.left.indicatorId}".`
      );
    }
    if (condition.right.kind === "indicator" && !knownIndicatorIds.has(condition.right.indicatorId)) {
      errors.push(
        `Condition ${index + 1}: Right operand references unknown indicator "${condition.right.indicatorId}".`
      );
    }
  });
  return errors;
}

// src/risk.schema.ts
import { z as z3 } from "zod";
var stopLossSchema = z3.object({
  mode: z3.enum(["pips", "atr_multiple", "percent", "none"]),
  value: z3.number().min(0),
  atrPeriod: z3.number().int().min(1).max(500).default(14).optional(),
  noneConfirmed: z3.boolean().optional()
}).superRefine((sl, ctx) => {
  if (sl.mode === "none" && sl.noneConfirmed !== true) {
    ctx.addIssue({
      code: z3.ZodIssueCode.custom,
      message: "You must confirm the risk of running without a Stop Loss.",
      path: ["noneConfirmed"]
    });
  }
  if (sl.mode === "pips" && sl.value <= 0) {
    ctx.addIssue({
      code: z3.ZodIssueCode.custom,
      message: "Stop Loss in pips must be greater than 0.",
      path: ["value"]
    });
  }
  if (sl.mode === "atr_multiple") {
    if (sl.value <= 0) {
      ctx.addIssue({
        code: z3.ZodIssueCode.custom,
        message: "ATR multiplier must be greater than 0.",
        path: ["value"]
      });
    }
    if (sl.atrPeriod !== void 0 && sl.atrPeriod < 1) {
      ctx.addIssue({
        code: z3.ZodIssueCode.custom,
        message: "ATR period must be at least 1 bar.",
        path: ["atrPeriod"]
      });
    }
  }
  if (sl.mode === "percent" && (sl.value <= 0 || sl.value > 100)) {
    ctx.addIssue({
      code: z3.ZodIssueCode.custom,
      message: "Percent stop loss must be between 0.01% and 100%.",
      path: ["value"]
    });
  }
});
var takeProfitSchema = z3.object({
  mode: z3.enum(["pips", "atr_multiple", "rr_ratio", "none"]),
  value: z3.number().min(0),
  atrPeriod: z3.number().int().min(1).max(500).default(14).optional()
}).superRefine((tp, ctx) => {
  if (tp.mode !== "none" && tp.value <= 0) {
    ctx.addIssue({
      code: z3.ZodIssueCode.custom,
      message: "Take Profit value must be greater than 0.",
      path: ["value"]
    });
  }
  if (tp.mode === "atr_multiple" && tp.atrPeriod !== void 0 && tp.atrPeriod < 1) {
    ctx.addIssue({
      code: z3.ZodIssueCode.custom,
      message: "ATR period must be at least 1 bar.",
      path: ["atrPeriod"]
    });
  }
});
var trailingStopSchema = z3.object({
  enabled: z3.boolean(),
  mode: z3.enum(["classic", "step", "atr"]),
  distance: z3.number().min(0),
  step: z3.number().min(0).optional(),
  atrPeriod: z3.number().int().min(1).max(500).default(14).optional()
}).superRefine((ts, ctx) => {
  if (!ts.enabled) return;
  if (ts.distance <= 0) {
    ctx.addIssue({
      code: z3.ZodIssueCode.custom,
      message: "Trailing stop distance must be greater than 0.",
      path: ["distance"]
    });
  }
  if (ts.mode === "step" && (ts.step === void 0 || ts.step <= 0)) {
    ctx.addIssue({
      code: z3.ZodIssueCode.custom,
      message: "Step size is required and must be > 0 for step-based trailing stop.",
      path: ["step"]
    });
  }
  if (ts.mode === "atr" && ts.atrPeriod !== void 0 && ts.atrPeriod < 1) {
    ctx.addIssue({
      code: z3.ZodIssueCode.custom,
      message: "ATR period must be at least 1 bar.",
      path: ["atrPeriod"]
    });
  }
});
var breakEvenSchema = z3.object({
  enabled: z3.boolean(),
  triggerPips: z3.number().min(0),
  lockPips: z3.number().min(0)
}).superRefine((be, ctx) => {
  if (!be.enabled) return;
  if (be.triggerPips <= 0) {
    ctx.addIssue({
      code: z3.ZodIssueCode.custom,
      message: "Break-even trigger must be greater than 0 pips.",
      path: ["triggerPips"]
    });
  }
});
var otherExitsSchema = z3.object({
  exitOnOppositeSignal: z3.boolean().default(false),
  closeAfterNBars: z3.number().int().min(1).max(1e4).nullable().default(null),
  closeAtSessionEnd: z3.boolean().default(false)
});
var moneySchema = z3.object({
  mode: z3.enum(["fixed_lots", "risk_percent", "fixed_dollar"]),
  value: z3.number(),
  maxLot: z3.number().min(0.01).max(1e3),
  martingaleMultiplier: z3.number().min(1).max(10).default(1)
}).superRefine((money, ctx) => {
  if (money.mode === "fixed_lots") {
    if (money.value < 0.01 || money.value > 1e3) {
      ctx.addIssue({
        code: z3.ZodIssueCode.custom,
        message: "Fixed lot size must be between 0.01 and 1000.",
        path: ["value"]
      });
    }
  }
  if (money.mode === "risk_percent") {
    if (money.value < 0.01 || money.value > 10) {
      ctx.addIssue({
        code: z3.ZodIssueCode.custom,
        message: "Risk percent must be between 0.01% and 10%.",
        path: ["value"]
      });
    }
  }
  if (money.mode === "fixed_dollar" && money.value <= 0) {
    ctx.addIssue({
      code: z3.ZodIssueCode.custom,
      message: "Fixed dollar risk must be greater than 0.",
      path: ["value"]
    });
  }
});
var managementSchema = z3.object({
  maxOpenTrades: z3.number().int().min(1).max(100),
  oneTradePerBar: z3.boolean().default(true),
  maxTradesPerDay: z3.number().int().min(0).max(1e3).default(0),
  allowHedging: z3.boolean().default(false)
});
var exitConfigSchema = z3.object({
  stopLoss: stopLossSchema,
  takeProfit: takeProfitSchema,
  trailing: trailingStopSchema,
  breakEven: breakEvenSchema,
  other: otherExitsSchema
});
function detectRiskWarnings(exit, money) {
  const warnings = [];
  if (money.mode === "risk_percent" && money.value > 3) {
    warnings.push(`\u26A0 Risk per trade is ${money.value}% \u2014 above the recommended 3% maximum.`);
  }
  if (money.martingaleMultiplier > 1) {
    warnings.push("\u26A0 Martingale multiplier is enabled. This can compound losses rapidly.");
  }
  if (exit.takeProfit.mode === "none" && !exit.trailing.enabled) {
    warnings.push("\u26A0 No Take Profit and no Trailing Stop \u2014 trades have no defined exit target.");
  }
  if (exit.stopLoss.mode === "atr_multiple" && exit.stopLoss.value < 0.5) {
    warnings.push("\u26A0 ATR Stop Loss multiplier is below 0.5 \u2014 this may be too tight and cause frequent stop-outs.");
  }
  return warnings;
}

// src/filter.schema.ts
import { z as z4 } from "zod";
var timeStringSchema = z4.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Time must be in HH:mm 24-hour format (e.g. "08:00").');
var sessionWindowSchema = z4.object({
  start: timeStringSchema,
  end: timeStringSchema,
  tz: z4.string().min(1).max(50).default("UTC")
}).superRefine((session, ctx) => {
  const toMinutes = (t) => {
    const [h = 0, m = 0] = t.split(":").map(Number);
    return h * 60 + m;
  };
  if (toMinutes(session.start) >= toMinutes(session.end)) {
    ctx.addIssue({
      code: z4.ZodIssueCode.custom,
      message: "Session start time must be before end time.",
      path: ["start"]
    });
  }
});
var weekDaySchema = z4.union([
  z4.literal(1),
  z4.literal(2),
  z4.literal(3),
  z4.literal(4),
  z4.literal(5),
  z4.literal(6),
  z4.literal(7)
]);
var filtersConfigSchema = z4.object({
  sessions: z4.array(sessionWindowSchema).max(5, "A maximum of 5 session windows is allowed."),
  days: z4.array(weekDaySchema).min(1, "At least one trading day must be selected.").max(7),
  maxSpreadPoints: z4.number().int().min(0).max(1e4).default(0),
  minBarsBetweenTrades: z4.number().int().min(0).max(1e4).default(0)
});
var advancedConfigSchema = z4.object({
  magicNumber: z4.number().int().min(1).max(2147483647).default(100001),
  slippagePoints: z4.number().int().min(0).max(1e3).default(3),
  orderComment: z4.string().max(64, "Order comment cannot exceed 64 characters (MQL limit).").default("ForexBuilder EA"),
  retryAttempts: z4.number().int().min(0).max(10).default(3)
});

// src/strategy.schema.ts
import { z as z5 } from "zod";
var SUPPORTED_SYMBOLS = [
  "EURUSD",
  "GBPUSD",
  "USDJPY",
  "USDCHF",
  "AUDUSD",
  "USDCAD",
  "NZDUSD",
  "EURJPY",
  "XAUUSD"
];
var SUPPORTED_TIMEFRAMES = ["M1", "M5", "M15", "M30", "H1", "H4", "D1"];
var strategyMetaSchema = z5.object({
  name: z5.string().min(3, "Strategy name must be at least 3 characters.").max(60, "Strategy name cannot exceed 60 characters."),
  description: z5.string().max(500).default(""),
  symbol: z5.enum(SUPPORTED_SYMBOLS),
  timeframe: z5.enum(SUPPORTED_TIMEFRAMES),
  direction: z5.enum(["both", "long_only", "short_only"]),
  version: z5.number().int().min(1),
  // Backtest simulation parameters
  initialCapital: z5.number().min(10).optional().default(1e4),
  leverage: z5.number().int().min(1).max(1e3).optional().default(100),
  startDate: z5.string().optional().default("2023-01-01"),
  endDate: z5.string().optional().default("2024-01-01")
});
var strategyJsonSchema = z5.object({
  meta: strategyMetaSchema,
  indicators: z5.array(indicatorInstanceSchema).max(20, "A maximum of 20 indicators is allowed per strategy."),
  entry: z5.object({
    long: directionalRulesSchema,
    short: directionalRulesSchema
  }),
  exit: exitConfigSchema,
  money: moneySchema,
  management: managementSchema,
  filters: filtersConfigSchema,
  advanced: advancedConfigSchema
}).superRefine((strategy, ctx) => {
  const indicatorIds = strategy.indicators.map((i) => i.id);
  const seen = /* @__PURE__ */ new Set();
  indicatorIds.forEach((id, idx) => {
    if (seen.has(id)) {
      ctx.addIssue({
        code: z5.ZodIssueCode.custom,
        message: `Duplicate indicator ID "${id}" \u2014 each indicator instance must have a unique ID.`,
        path: ["indicators", idx, "id"]
      });
    }
    seen.add(id);
    const ind = strategy.indicators[idx];
    if (ind && ind.params.type === "MACD") {
      if (ind.params.fastPeriod >= ind.params.slowPeriod) {
        ctx.addIssue({
          code: z5.ZodIssueCode.custom,
          message: "MACD fast period must be less than slow period.",
          path: ["indicators", idx, "params", "fastPeriod"]
        });
      }
    }
  });
  const knownIds = new Set(indicatorIds);
  const direction = strategy.meta.direction;
  if (direction !== "short_only") {
    if (strategy.entry.long.conditions.length === 0 && (strategy.entry.long.patterns?.length ?? 0) === 0) {
      ctx.addIssue({
        code: z5.ZodIssueCode.custom,
        message: "Long entry rules must have at least one condition or candle pattern.",
        path: ["entry", "long", "conditions"]
      });
    }
    const longErrors = validateIndicatorReferences(strategy.entry.long, knownIds);
    longErrors.forEach((msg) => {
      ctx.addIssue({ code: z5.ZodIssueCode.custom, message: msg, path: ["entry", "long"] });
    });
  }
  if (direction !== "long_only") {
    if (strategy.entry.short.conditions.length === 0 && (strategy.entry.short.patterns?.length ?? 0) === 0) {
      ctx.addIssue({
        code: z5.ZodIssueCode.custom,
        message: "Short entry rules must have at least one condition or candle pattern.",
        path: ["entry", "short", "conditions"]
      });
    }
    const shortErrors = validateIndicatorReferences(strategy.entry.short, knownIds);
    shortErrors.forEach((msg) => {
      ctx.addIssue({ code: z5.ZodIssueCode.custom, message: msg, path: ["entry", "short"] });
    });
  }
});
function validateStrategy(data) {
  const result = strategyJsonSchema.safeParse(data);
  const errors = [];
  const warnings = [];
  if (!result.success) {
    result.error.issues.forEach((issue) => {
      errors.push({
        path: issue.path.join("."),
        message: issue.message,
        severity: "error"
      });
    });
  }
  if (result.success) {
    const warningMessages = detectRiskWarnings(result.data.exit, result.data.money);
    const longConditionCount = result.data.entry.long.conditions.length;
    const shortConditionCount = result.data.entry.short.conditions.length;
    if (longConditionCount > 12) {
      warningMessages.push(
        `\u26A0 Long entry has ${longConditionCount} conditions \u2014 a large number of conditions may indicate over-fitting.`
      );
    }
    if (shortConditionCount > 12) {
      warningMessages.push(
        `\u26A0 Short entry has ${shortConditionCount} conditions \u2014 a large number of conditions may indicate over-fitting.`
      );
    }
    warningMessages.forEach((msg) => {
      warnings.push({ path: "", message: msg, severity: "warning" });
    });
  }
  return {
    isValid: result.success && errors.length === 0,
    errors,
    warnings,
    data: result.success ? result.data : null
  };
}
export {
  advancedConfigSchema,
  adxParamsSchema,
  atrParamsSchema,
  bbParamsSchema,
  breakEvenSchema,
  candlePatternSchema,
  cciParamsSchema,
  conditionOperandSchema,
  conditionOperatorSchema,
  detectRiskWarnings,
  directionalRulesSchema,
  emaParamsSchema,
  exitConfigSchema,
  filtersConfigSchema,
  ichimokuParamsSchema,
  indicatorInstanceSchema,
  indicatorParamsSchema,
  macdParamsSchema,
  managementSchema,
  momentumParamsSchema,
  moneySchema,
  otherExitsSchema,
  psarParamsSchema,
  rsiParamsSchema,
  sessionWindowSchema,
  smaParamsSchema,
  stochParamsSchema,
  stopLossSchema,
  strategyConditionSchema,
  strategyJsonSchema,
  takeProfitSchema,
  trailingStopSchema,
  validateIndicatorReferences,
  validateStrategy,
  weekDaySchema
};
